import { Constants } from 'constants/api';
import { forwardToLogRocket } from 'utils/logrocket';

// Client-side diagnostics for scanner problems reported from customer devices
// (e.g. SDK errors 7006 / 7014).
// - Every entry is kept in a localStorage ring buffer (viewable at /diagnostics).
// - warn/error entries are also POSTed to Constants.API.CLIENT_LOGS with a
//   device snapshot, so they can be looked up by the "Ref" shown to the user.

export type LogLevel = 'info' | 'warn' | 'error';

export interface LogEntry {
  ts: string;
  level: LogLevel;
  event: string;
  details?: Record<string, unknown>;
  route: string;
  pageRef: string;
  msSincePageLoad: number;
}

const STORAGE_KEY = 'zakDiagnosticsLog';
const MAX_ENTRIES = 200;
const APP_VERSION: string = require('../../package.json').version;

// One id per page load, shown to the user next to error codes so support
// can match a customer's screenshot with the stored / uploaded logs.
const pageRef = `${Date.now().toString(36)}-${Math.random()
  .toString(36)
  .slice(2, 6)}`.toUpperCase();

let deviceInfoPromise: Promise<Record<string, unknown>> | undefined;
let deviceInfoSentForPage = false;

export const getPageRef = () => pageRef;

const safeParse = (value: string | null) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

// Keep the uploaded payload JSON-safe and small (Errors don't stringify).
export const describeError = (e: any) => {
  if (!e) return e;
  if (typeof e !== 'object') return { value: String(e) };
  return {
    name: e.name,
    message: e.message,
    errorCode: e.errorCode,
    code: e.code,
    domain: e.domain,
    stack: typeof e.stack === 'string' ? e.stack.slice(0, 2000) : undefined,
  };
};

const getUserContext = () => {
  const user = safeParse(localStorage.getItem('userDetails'));
  const client = safeParse(localStorage.getItem('clientDetails'));
  return {
    userId: user?.id,
    clientId: user?.client_id ?? client?.id,
  };
};

export const collectDeviceInfo = (): Promise<Record<string, unknown>> => {
  if (!deviceInfoPromise) {
    deviceInfoPromise = (async () => {
      const nav: any = navigator;
      const navEntry: any = performance.getEntriesByType?.('navigation')?.[0];
      let uaData: Record<string, unknown> | undefined;
      try {
        // bitness tells whether the browser is 32-bit, the usual cause of 7014
        uaData = await nav.userAgentData?.getHighEntropyValues?.([
          'architecture',
          'bitness',
          'model',
          'platformVersion',
          'fullVersionList',
        ]);
      } catch {
        uaData = undefined;
      }
      return {
        appVersion: APP_VERSION,
        userAgent: navigator.userAgent,
        uaData,
        deviceMemoryGB: nav.deviceMemory,
        cpuCores: navigator.hardwareConcurrency,
        crossOriginIsolated: (window as any).crossOriginIsolated,
        sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
        webAssembly: typeof WebAssembly !== 'undefined',
        jsHeap: memorySnapshot(),
        screen: `${screen.width}x${screen.height}@${window.devicePixelRatio}`,
        connection: nav.connection
          ? {
              effectiveType: nav.connection.effectiveType,
              downlinkMbps: nav.connection.downlink,
              rttMs: nav.connection.rtt,
              saveData: nav.connection.saveData,
            }
          : undefined,
        navigationType: navEntry?.type,
        language: navigator.language,
      };
    })();
  }
  return deviceInfoPromise;
};

// Current JS heap (Chrome only); useful next to memory errors like 7014.
export const memorySnapshot = () => {
  const m: any = (performance as any).memory;
  return m
    ? {
        limitMB: Math.round(m.jsHeapSizeLimit / 1048576),
        totalMB: Math.round(m.totalJSHeapSize / 1048576),
        usedMB: Math.round(m.usedJSHeapSize / 1048576),
      }
    : undefined;
};

export const readLogs = (): LogEntry[] => {
  try {
    return safeParse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

export const clearLogs = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

const store = (entry: LogEntry) => {
  try {
    const entries = readLogs();
    entries.push(entry);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(entries.slice(-MAX_ENTRIES)),
    );
  } catch {
    // Storage full or blocked: logging must never break the app
  }
};

const upload = async (entry: LogEntry) => {
  try {
    const payload: Record<string, unknown> = {
      ...entry,
      ...getUserContext(),
      appVersion: APP_VERSION,
      url: window.location.href,
    };
    // Send the full device snapshot once per page load; it doesn't change.
    if (!deviceInfoSentForPage) {
      payload.device = await collectDeviceInfo();
      deviceInfoSentForPage = true;
    }
    await fetch(Constants.API.CLIENT_LOGS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Offline or endpoint unavailable: the entry is still in localStorage
  }
};

export const log = (
  level: LogLevel,
  event: string,
  details?: Record<string, unknown>,
) => {
  const entry: LogEntry = {
    ts: new Date().toISOString(),
    level,
    event,
    details,
    route: window.location.pathname,
    pageRef,
    msSincePageLoad: Math.round(performance.now()),
  };
  store(entry);
  const consoleFn =
    level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
  consoleFn(`[diag] ${event}`, details ?? '');
  if (level !== 'info') {
    upload(entry);
    forwardToLogRocket(level, event, details, pageRef);
  }
};

export const logger = {
  info: (event: string, details?: Record<string, unknown>) =>
    log('info', event, details),
  warn: (event: string, details?: Record<string, unknown>) =>
    log('warn', event, details),
  error: (event: string, details?: Record<string, unknown>) =>
    log('error', event, details),
};

let initialized = false;

// Call once at startup: records the device and catches uncaught errors.
export const initLogging = () => {
  if (initialized) return;
  initialized = true;

  window.addEventListener('error', (event) => {
    logger.error('window_error', {
      message: event.message,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
      error: describeError(event.error),
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('unhandled_rejection', { reason: describeError(event.reason) });
  });

  collectDeviceInfo().then((device) => logger.info('app_start', { device }));
};
